import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { getMe, loginUser, registerUser, setToken, logoutApi } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = signed out, object = signed in
  const [user, setUser] = useState(null);

  const refreshUser = useCallback(async () => {
    // Sends the JWT bearer (localStorage) and/or the Emergent session cookie.
    try { const u = await getMe(); setUser(u); return u; }
    catch (e) {
      // Only a genuine 401 signs the user out (clears token). Network/transient
      // errors must not wipe a valid token during concurrent/mount races.
      if (e?.response?.status === 401) setToken(null);
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from the Google OAuth callback, skip the /me check —
    // AuthCallback exchanges the session_id and establishes the session first.
    if (window.location.hash?.includes("session_id=")) { return; }
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email, password) => {
    const data = await loginUser({ email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await registerUser(payload);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await logoutApi();       // clears the Emergent session + httpOnly cookie
    setToken(null);          // clears the JWT / bearer token
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, register, logout, refreshUser }),
    [user, login, register, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
